import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useState, lazy, Suspense } from 'react'
import { LandingPage } from './components/pages/LandingPage'
import ErrorBoundary from './components/common/ErrorBoundary'
import ResultsPageErrorBoundary from './components/common/ResultsPageErrorBoundary'
import LoadingSpinner from './components/common/LoadingSpinner'
import { AuthProvider } from './contexts/AuthContext'
import { UserProvider } from './contexts/UserContext'
import ProtectedRoute from './components/auth/ProtectedRoute'
import { ToastProvider } from './contexts/ToastContext'
import { authService } from './services/authService'

// 延迟加载评估组件 - 这些是最大的组件
const AssessmentManager = lazy(() => import('./components/assessment/AssessmentManager'))
const CognitionAssessment = lazy(() => import('./components/assessment/CognitionAssessment'))
const CognitionAssessmentDetail = lazy(() => import('./components/assessment/CognitionAssessmentDetail'))
const MobilityAssessment = lazy(() => import('./components/assessment/MobilityAssessment'))
const MobilityAssessmentDetail = lazy(() => import('./components/assessment/MobilityAssessmentDetail'))
const GenericAssessmentDetail = lazy(() => import('./components/assessment/GenericAssessmentDetail'))
const NutritionAssessmentDetail = lazy(() => import('./components/assessment/NutritionAssessmentDetail'))
const PsychologicalAssessmentDetail = lazy(() => import('./components/assessment/PsychologicalAssessmentDetail'))
const VitalityAssessment = lazy(() => import('./components/assessment/VitalityAssessment'))
const VisionAssessment = lazy(() => import('./components/assessment/VisionAssessment'))
const HearingAssessment = lazy(() => import('./components/assessment/HearingAssessment'))
const PsychologicalAssessment = lazy(() => import('./components/assessment/PsychologicalAssessment'))
const SocialCareAssessment = lazy(() => import('./components/assessment/SocialCareAssessment'))
const CarerSupportAssessment = lazy(() => import('./components/assessment/CarerSupportAssessment'))
const UrinaryIncontinenceAssessment = lazy(() => import('./components/assessment/UrinaryIncontinenceAssessment'))

// 延迟加载结果页面组件
const ResultsPage = lazy(() => import('./components/pages/ResultsPage').then(module => ({ default: module.ResultsPage })))
const EnhancedResultsPage = lazy(() => import('./components/pages/EnhancedResultsPage'))

// 延迟加载其他页面组件
const ConsultantPage = lazy(() => import('./components/pages/ConsultantPage').then(module => ({ default: module.ConsultantPage })))
const PlanGeneratorWizard = lazy(() => import('./components/pages/PlanGeneratorWizard'))
const PersonalizedPlanPage = lazy(() => import('./components/pages/PersonalizedPlanPage'))
const DevTestPage = lazy(() => import('./components/pages/DevTestPage'))
const UserSelectionPage = lazy(() => import('./components/pages/UserSelectionPage'))
const UserProfilePage = lazy(() => import('./components/pages/UserProfilePage'))
const AdminDashboard = lazy(() => import('./components/pages/AdminDashboard'))
const DataMigrationTool = lazy(() => import('./components/admin/DataMigrationTool'))
const AssessmentUserSelectionPage = lazy(() => import('./components/pages/AssessmentUserSelectionPage'))
const QuickTestPage = lazy(() => import('./components/testing/QuickTestPage'))
const QuickTestResultPage = lazy(() => import('./components/testing/QuickTestResultPage'))
const FunctionalCapabilityTestPage = lazy(() => import('./pages/FunctionalCapabilityTestPage').then(module => ({ default: module.FunctionalCapabilityTestPage })))
const AuthPage = lazy(() => import('./components/auth/AuthPage'))
const AssessmentChoicePage = lazy(() => import('./components/pages/AssessmentChoicePage'))
const StandaloneNeedsAssessmentPage = lazy(() => import('./components/pages/StandaloneNeedsAssessmentPage'))
const BenefitProfileMaintenancePage = lazy(() => import('./components/pages/BenefitProfileMaintenancePage'))
const AIAssistantTestPage = lazy(() => import('./pages/AIAssistantTestPage'))
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'))
const AIAssistantPageNew = lazy(() => import('./pages/AIAssistantPageNew'))
const AIAssistantPageIntegrated = lazy(() => import('./pages/AIAssistantPageIntegrated'))
const AIAssistantPageRefactored = lazy(() => import('./pages/AIAssistantPageRefactored'))
const NeedsResultsPage = lazy(() => import('./components/pages/NeedsResultsPage'))
const Stage1And2Demo = lazy(() => import('./components/ai-assistant/Stage1And2Demo'))
const AssessmentCardsDemo = lazy(() => import('./components/ai-assistant/AssessmentCardsDemo'))
const Stage4Demo = lazy(() => import('./components/ai-assistant/Stage4Demo'))
const Stage5Demo = lazy(() => import('./components/ai-assistant/Stage5Demo'))
const Stage6Demo = lazy(() => import('./components/ai-assistant/Stage6Demo'))
const ServicePackageDetailPage = lazy(() => import('./pages/ServicePackageDetailPage').then(module => ({ default: module.ServicePackageDetailPage })))

import { ComprehensiveAssessmentResult, PersonalizedPlan } from './types/index'

// ResultsPage wrapper to handle both state-based and location-based assessment results
function ResultsPageWrapper({ assessmentResult, navigate }: {
  assessmentResult: ComprehensiveAssessmentResult | null,
  navigate: any
}) {
  const location = useLocation();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const locationState = location.state as {
    assessmentResult?: ComprehensiveAssessmentResult;
    userProfile?: any;
    isHistoryView?: boolean;
  } | null;

  // 🔧 修复：如果URL中有assessmentId参数，说明是从外部链接（如AI助手）进入
  // 应该传递null让EnhancedResultsPage自己从服务器加载数据
  // 只有在没有URL参数时才使用state/prop中的评估结果（来自刚完成的评估）
  const activeAssessmentResult = assessmentId
    ? null
    : (locationState?.assessmentResult || assessmentResult);

  // 当有URL参数时，始终渲染EnhancedResultsPage（它会自己加载数据）
  // 当没有URL参数但有评估结果时，也渲染
  if (assessmentId || activeAssessmentResult) {
    return (
      <Suspense fallback={<LoadingSpinner message="正在生成个性化评估报告..." />}>
        <EnhancedResultsPage
          assessmentResult={activeAssessmentResult}
        />
      </Suspense>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">评估结果未找到</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          请先完成ICOPE能力评估以查看您的个性化结果。
        </p>
        <div className="space-x-4">
          <button
            onClick={() => navigate('/assessment-choice')}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            开始评估
          </button>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const [assessmentResult, setAssessmentResult] = useState<ComprehensiveAssessmentResult | null>(null)
  const [personalizedPlan, setPersonalizedPlan] = useState<PersonalizedPlan | null>(null)
  const navigate = useNavigate()

  const handleAssessmentComplete = async (result: ComprehensiveAssessmentResult) => {
    console.log('📊 评估完成，开始处理结果:', result.sessionId);
    setAssessmentResult(result);

    // UserContext将自动处理用户状态管理
    navigate('/results');
  }


  const handlePlanGenerated = (plan: PersonalizedPlan) => {
    setPersonalizedPlan(plan)
    navigate('/plan')
  }

  // 测试场景处理函数
  const handleTestScenario = (result: ComprehensiveAssessmentResult, profile: any) => {
    console.log('🧪 设置测试数据:', { sessionId: result.sessionId, userProfile: profile });
    setAssessmentResult(result);
    // 用户资料将由UserContext管理
  }

  return (
    <div className="min-h-screen bg-gray-50">
          
      <Routes>
        <Route path="/" element={<LandingPage />} />
        
        {/* 认证系统路由 */}
        <Route path="/auth" element={
          <Suspense fallback={<LoadingSpinner message="正在加载认证页面..." />}>
            <AuthPage />
          </Suspense>
        } />
        
        {/* 开发测试模式 - 仅在开发环境显示 */}
        {process.env.NODE_ENV === 'development' && (
          <>
            <Route
              path="/dev-test"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载测试页面..." />}>
                  <DevTestPage onTestScenario={handleTestScenario} />
                </Suspense>
              }
            />
            <Route
              path="/quick-test"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载快速测试..." />}>
                  <QuickTestPage />
                </Suspense>
              }
            />
            <Route
              path="/quick-test-results"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载快速测试结果..." />}>
                  <QuickTestResultPage />
                </Suspense>
              }
            />
            <Route
              path="/ai-assistant-test"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手测试..." />}>
                  <AIAssistantTestPage />
                </Suspense>
              }
            />
            <Route
              path="/ai-assistant/:userId/:assessmentId"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手..." />}>
                  <AIAssistantPage />
                </Suspense>
              }
            />
            <Route
              path="/ai-assistant/:userId"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手..." />}>
                  <AIAssistantPage />
                </Suspense>
              }
            />
            <Route
              path="/ai-assistant-new/:userId/:assessmentId"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手（新版）..." />}>
                  <AIAssistantPageNew />
                </Suspense>
              }
            />
            <Route
              path="/ai-assistant-new/:userId"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手（新版）..." />}>
                  <AIAssistantPageNew />
                </Suspense>
              }
            />

            {/* AI助手集成版 - 支持完整收藏功能 */}
            <Route
              path="/ai-assistant-integrated/:userId/:assessmentId"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手（集成版）..." />}>
                  <AIAssistantPageIntegrated />
                </Suspense>
              }
            />
            <Route
              path="/ai-assistant-integrated/:userId"
              element={
                <Suspense fallback={<LoadingSpinner message="正在加载AI助手（集成版）..." />}>
                  <AIAssistantPageIntegrated />
                </Suspense>
              }
            />

          </>
        )}

        {/* AI助手生产路由 - 适用于所有环境 */}
        <Route
          path="/ai-assistant-v2/:assessmentId"
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载AI助手..." />}>
              <AIAssistantPageRefactored />
            </Suspense>
          }
        />
        <Route
          path="/ai-assistant-v2"
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载AI助手..." />}>
              <AIAssistantPageRefactored />
            </Suspense>
          }
        />

        {/* 用户档案系统路由 - 需要认证 */}
        <Route path="/users" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载用户页面..." />}>
              <UserSelectionPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载用户资料..." />}>
              <UserProfilePage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/benefit-profile" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载福利画像..." />}>
              <BenefitProfileMaintenancePage />
            </Suspense>
          </ProtectedRoute>
        } />

        <Route path="/admin" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载管理面板..." />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/admin/add-user" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载管理面板..." />}>
              <AdminDashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/admin/migration" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载数据迁移工具..." />}>
              <DataMigrationTool />
            </Suspense>
          </ProtectedRoute>
        } />
        
        {/* 功能能力评估路由 - 测试页面，无需认证 */}
        <Route path="/functional-capability-test" element={
          <Suspense fallback={<LoadingSpinner message="正在加载功能能力评估..." />}>
            <FunctionalCapabilityTestPage />
          </Suspense>
        } />

        {/* AI助手UI升级演示页面 - 阶段1+2 */}
        <Route path="/ai-assistant-demo" element={
          <Suspense fallback={<LoadingSpinner message="正在加载演示页面..." />}>
            <Stage1And2Demo />
          </Suspense>
        } />

        {/* AI助手AssessmentCards组件演示 - 阶段3 */}
        <Route path="/assessment-cards-demo" element={
          <Suspense fallback={<LoadingSpinner message="正在加载评估卡片演示..." />}>
            <AssessmentCardsDemo />
          </Suspense>
        } />

        {/* AI助手HistoryPanel组件演示 - 阶段4 */}
        <Route path="/history-panel-demo" element={
          <Suspense fallback={<LoadingSpinner message="正在加载历史面板演示..." />}>
            <Stage4Demo />
          </Suspense>
        } />

        {/* AI助手对话组件演示 - 阶段5 */}
        <Route path="/conversation-demo" element={
          <Suspense fallback={<LoadingSpinner message="正在加载对话演示..." />}>
            <Stage5Demo />
          </Suspense>
        } />

        {/* AI助手快捷操作与弹窗演示 - 阶段6 */}
        <Route path="/modal-demo" element={
          <Suspense fallback={<LoadingSpinner message="正在加载弹窗演示..." />}>
            <Stage6Demo />
          </Suspense>
        } />

        {/* 服务推荐详情页路由 */}
        <Route path="/service-package/:packageId" element={
          <Suspense fallback={<LoadingSpinner message="正在加载服务包详情..." />}>
            <ServicePackageDetailPage />
          </Suspense>
        } />

        {/* 评估系统路由 */}
        {/* 新的评估选择入口 */}
        <Route path="/assessment-choice" element={
          <Suspense fallback={<LoadingSpinner message="正在加载评估选择..." />}>
            <AssessmentChoicePage />
          </Suspense>
        } />
        
        {/* 独立的需求搜集评估 - 需要认证 */}
        <Route path="/needs-assessment" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner message="正在加载需求搜集评估..." />}>
              <StandaloneNeedsAssessmentPage />
            </Suspense>
          </ProtectedRoute>
        } />
        
        <Route path="/assessment-start" element={
          <Suspense fallback={<LoadingSpinner message="正在加载评估选择..." />}>
            <AssessmentUserSelectionPage />
          </Suspense>
        } />
        <Route
          path="/assessment"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner message="正在加载能力评估..." />}>
                <ErrorBoundary>
                  <AssessmentManager
                    onComplete={handleAssessmentComplete}
                    userId={sessionStorage.getItem('selectedUserId') || undefined}
                  />
                </ErrorBoundary>
              </Suspense>
            </ProtectedRoute>
          }
        />
        
        {/* 单独的评估组件路由 - 只读模式 */}
        <Route 
          path="/assessment/cognition" 
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载认知评估详情..." />}>
              <CognitionAssessmentDetail 
                assessmentData={assessmentResult?.intrinsicCapacity?.cognition}
              />
            </Suspense>
          } 
        />
        <Route 
          path="/assessment/mobility" 
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载行动评估详情..." />}>
              <MobilityAssessmentDetail 
                assessmentData={assessmentResult?.intrinsicCapacity?.mobility}
              />
            </Suspense>
          } 
        />
        <Route 
          path="/assessment/vitality" 
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载营养评估详情..." />}>
              <NutritionAssessmentDetail 
                assessmentData={assessmentResult?.intrinsicCapacity?.vitality}
              />
            </Suspense>
          } 
        />
        <Route 
          path="/assessment/vision" 
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载视力评估详情..." />}>
              <GenericAssessmentDetail 
                assessmentData={assessmentResult?.intrinsicCapacity?.vision}
                title="视力健康"
                filterQuestion="您在看远处或近处的物体时有困难吗？"
                testItems={[
                  { name: "远视力测试", description: "测试看远处物体的清晰度" },
                  { name: "近视力测试", description: "测试阅读或看近处物体的能力" }
                ]}
              />
            </Suspense>
          } 
        />
        <Route 
          path="/assessment/hearing" 
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载听力评估详情..." />}>
              <GenericAssessmentDetail 
                assessmentData={assessmentResult?.intrinsicCapacity?.hearing}
                title="听力健康"
                filterQuestion="您在听对话时有困难吗？"
                testItems={[
                  { name: "耳语测试", description: "在安静环境中测试对轻声说话的听力" }
                ]}
              />
            </Suspense>
          } 
        />
        <Route 
          path="/assessment/psychological" 
          element={
            <Suspense fallback={<LoadingSpinner message="正在加载心理评估详情..." />}>
              <PsychologicalAssessmentDetail 
                assessmentData={assessmentResult?.intrinsicCapacity?.psychologicalCapacity}
              />
            </Suspense>
          } 
        />
        {/* 评估结果页面 - 支持URL参数方式 (推荐) */}
        <Route
          path="/results/:assessmentId"
          element={
            <ResultsPageErrorBoundary>
              <ResultsPageWrapper
                assessmentResult={assessmentResult}
                navigate={navigate}
              />
            </ResultsPageErrorBoundary>
          }
        />
        {/* 评估结果页面 - 保留旧的state方式作为fallback */}
        <Route
          path="/results"
          element={
            <ResultsPageErrorBoundary>
              <ResultsPageWrapper
                assessmentResult={assessmentResult}
                navigate={navigate}
              />
            </ResultsPageErrorBoundary>
          }
        />

        {/* 需求评估结果页面 - 支持从AI助手导航到需求评估报告 */}
        <Route
          path="/needs-results/:reportId"
          element={
            <ResultsPageErrorBoundary>
              <Suspense fallback={<LoadingSpinner message="正在加载需求评估报告..." />}>
                <NeedsResultsPage />
              </Suspense>
            </ResultsPageErrorBoundary>
          }
        />

        <Route
          path="/results-wrapper" 
          element={
            <ResultsPageErrorBoundary>
              {assessmentResult ? (
                <Suspense fallback={<LoadingSpinner message="正在生成个性化评估报告..." />}>
                  <EnhancedResultsPage
                    assessmentResult={assessmentResult}
                  />
                </Suspense>
              ) : (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">评估结果未找到</h2>
                    <p className="text-gray-600 max-w-md mx-auto">
                      请先完成ICOPE能力评估以查看您的个性化结果。
                    </p>
                    <div className="space-x-4">
                      <button
                        onClick={() => navigate('/assessment-choice')}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        开始评估
                      </button>
                      <button
                        onClick={() => navigate('/')}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        返回首页
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </ResultsPageErrorBoundary>
          } 
        />
        <Route 
          path="/results-simple" 
          element={
            <ResultsPageErrorBoundary>
              {assessmentResult ? (
                <Suspense fallback={<LoadingSpinner message="正在加载评估报告..." />}>
                  <ResultsPage assessmentResult={assessmentResult} />
                </Suspense>
              ) : (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">评估结果未找到</h2>
                    <p className="text-gray-600 max-w-md mx-auto">
                      请先完成ICOPE能力评估以查看您的结果。
                    </p>
                    <div className="space-x-4">
                      <button
                        onClick={() => navigate('/assessment-choice')}
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        开始评估
                      </button>
                      <button
                        onClick={() => navigate('/')}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        返回首页
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </ResultsPageErrorBoundary>
          } 
        />
        <Route path="/consultant" element={
          <Suspense fallback={<LoadingSpinner message="正在加载能力顾问..." />}>
            <ConsultantPage />
          </Suspense>
        } />
        <Route 
          path="/plan-wizard" 
          element={assessmentResult ? (
            <Suspense fallback={<LoadingSpinner message="正在生成个性化照护计划..." />}>
              <PlanGeneratorWizard 
                assessmentResult={assessmentResult} 
                userProfile={{
                  ageRange: '70-80',
                  assessmentFor: 'self',
                  preferredContentLevel: 'standard'
                }}
                onPlanGenerated={handlePlanGenerated}
              />
            </Suspense>
          ) : (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">需要完成评估</h2>
                <p className="text-gray-600 max-w-md mx-auto">
                  计划生成需要基于您的ICOPE能力评估结果，请先完成评估。
                </p>
                <div className="space-x-4">
                  <button
                    onClick={() => navigate('/assessment-choice')}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    开始评估
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    返回首页
                  </button>
                </div>
              </div>
            </div>
          )} 
        />
        <Route 
          path="/plan" 
          element={personalizedPlan ? (
            <Suspense fallback={<LoadingSpinner message="正在加载个性化照护计划..." />}>
              <PersonalizedPlanPage plan={personalizedPlan} />
            </Suspense>
          ) : (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">个性化照护计划未找到</h2>
                <p className="text-gray-600 max-w-md mx-auto">
                  请先完成ICOPE能力评估，然后生成您的个性化照护计划。
                </p>
                <div className="space-x-4">
                  <button
                    onClick={() => navigate('/assessment-choice')}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    开始评估
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    返回首页
                  </button>
                </div>
              </div>
            </div>
          )} 
        />
      </Routes>
    </div>
  )
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <UserProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </UserProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
